import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import './index.css'
import './templates/templates.css'
import App from './App'
import { SITE } from './config/site'
import { registerServiceWorker } from './lib/registerSw'
import { warmRouteChunk } from './routeChunks'

/**
 * `/index.html` is the homepage file itself, and the host answers it with a
 * 200. Its static HTML already declares `canonical: /`, but the router has no
 * `/index.html` route: hydration mismatched (#418), the client swapped in the
 * 404 view, and useSeo rewrote the canonical to `/index.html` with `noindex`.
 * Normalising the address before the router reads it keeps that URL a clean
 * duplicate of `/`. A host-level 301 would be better still; that is a
 * public/.htaccess change.
 */
if (window.location.pathname === '/index.html') {
  window.history.replaceState(window.history.state, '', `/${window.location.search}${window.location.hash}`)
}

const container = document.getElementById('root')
if (!container) throw new Error('Root element missing')

const tree = (
  <StrictMode>
    <BrowserRouter basename={SITE.basePath === '/' ? undefined : SITE.basePath}>
      <App />
    </BrowserRouter>
  </StrictMode>
)

const mount = () => {
  if (container.dataset.prerendered === 'true' && container.firstElementChild) {
    hydrateRoot(container, tree)
  } else {
    createRoot(container).render(tree)
  }
  /* Offline support. Registered after load so it cannot compete with first paint. */
  registerServiceWorker()
}

/**
 * Marketing and landing routes are prerendered to static HTML at build time, so
 * they hydrate. The builder ships as an empty shell (its state comes from
 * localStorage, which does not exist during prerender), so it mounts fresh.
 * The marker attribute is written by scripts/prerender.ts.
 *
 * On a hydration path, the matched route's chunk is resolved and cached
 * first — see routeChunks.tsx for why a plain React.lazy() can't do this on
 * its own: without this, the first hydrateRoot render attempt suspends on
 * the not-yet-loaded chunk and produces a real hydration mismatch against
 * the server HTML (React error #418), not just a cosmetic flash.
 */
if (container.dataset.prerendered === 'true' && container.firstElementChild) {
  warmRouteChunk(window.location.pathname).finally(mount)
} else {
  mount()
}
